import { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate, Outlet } from 'react-router-dom';
import { logout, getCurrentSession, type UserRole } from '../../utils/auth';
import { useTestMode } from '../../hooks/useTestMode';

export default function AdminLayout() {
  const location = useLocation();
  const navigate = useNavigate();
  const session = getCurrentSession();
  const { isTestMode, toggleTestMode } = useTestMode();
  const [showMobileMenu, setShowMobileMenu] = useState(false);
  const [testRole, setTestRole] = useState<UserRole>(session?.role || 'admin');

  useEffect(() => {
    if (isTestMode) {
      const storedRole = localStorage.getItem('ski_service_test_role') as UserRole;
      if (storedRole) setTestRole(storedRole);
    } else {
      setTestRole(session?.role || 'admin');
    }
  }, [isTestMode, session?.role]);

  // Schließe Menü bei Routenwechsel
  useEffect(() => {
    setShowMobileMenu(false);
  }, [location.pathname]);

  const handleLogout = () => {
    logout();
    navigate('/kurier');
  };

  const handleRoleSwitch = (role: UserRole) => {
    localStorage.setItem('ski_service_test_role', role);
    setTestRole(role);
    window.location.reload();
  };

  const getNavigationItems = () => {
    const role = session?.role;
    if (role === 'admin') {
      return [
        { path: '/kurier/auftraege', label: 'Auftragsübersicht', icon: 'ri-file-list-3-line' },
        { path: '/kurier/einsatztage', label: 'Einsatztage', icon: 'ri-calendar-line' },
        { path: '/kurier/user-administration', label: 'Benutzerverwaltung', icon: 'ri-user-settings-line' },
        { path: '/kurier/einstellungen', label: 'Einstellungen', icon: 'ri-settings-3-line' },
      ];
    } else if (role === 'kurier') {
      return [
        { path: '/kurier/dashboard', label: 'Dashboard', icon: 'ri-dashboard-line' },
        { path: '/kurier/user-profile', label: 'Profil', icon: 'ri-user-line' },
      ];
    } else if (role === 'servicecenter') {
      return [
        { path: '/kurier/dashboard', label: 'Dashboard', icon: 'ri-dashboard-line' },
        { path: '/kurier/user-profile', label: 'Profil', icon: 'ri-user-line' },
      ];
    }
    return [];
  };

  const navigationItems = getNavigationItems();

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Test-Modus Banner */}
      {isTestMode && (
        <div className="fixed top-0 left-0 right-0 h-10 bg-red-600 text-white px-4 z-[9999] flex items-center">
          <div className="flex items-center justify-between w-full max-w-7xl mx-auto">
            <div className="flex items-center gap-2">
              <i className="ri-error-warning-line text-lg"></i>
              <span className="font-medium text-sm">⚠ TEST-MODUS AKTIV – Keine Live-Daten</span>
            </div>
            <div className="flex items-center gap-2">
              <span className="text-sm mr-2 hidden sm:inline">Rolle:</span>
              <div className="flex gap-1 bg-red-700 rounded-lg p-1">
                {(['admin', 'kurier', 'servicecenter'] as UserRole[]).map((r) => (
                  <button
                    key={r}
                    onClick={() => handleRoleSwitch(r)}
                    className={`px-2 py-1 rounded-md text-xs font-medium transition-colors whitespace-nowrap ${
                      testRole === r ? 'bg-white text-red-600' : 'text-white hover:bg-red-600'
                    }`}
                  >
                    {r === 'admin' ? 'Admin' : r === 'kurier' ? 'Kurier' : 'Service'}
                  </button>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ── MOBILE TOP NAVBAR ── */}
      <header
        className={`lg:hidden fixed left-0 right-0 z-[900] bg-white border-b border-gray-200 flex items-center justify-between px-4 h-14 ${
          isTestMode ? 'top-10' : 'top-0'
        }`}
      >
        <img
          src="https://static.readdy.ai/image/bd37f9b321d8d487bb2df5b36d703d09/c530b467e5a9713a9572ed21607c32d4.png"
          alt="Klosters Logo"
          className="h-8 w-auto"
        />
        <button
          onClick={() => setShowMobileMenu((v) => !v)}
          className="w-10 h-10 flex items-center justify-center rounded-lg text-gray-700 hover:bg-gray-100 transition-colors cursor-pointer"
          aria-label="Menü öffnen"
        >
          <i className={`text-2xl ${showMobileMenu ? 'ri-close-line' : 'ri-menu-line'}`}></i>
        </button>
      </header>

      {/* ── MOBILE DRAWER OVERLAY ── */}
      {showMobileMenu && (
        <div
          className="lg:hidden fixed inset-0 bg-black/40 z-[850]"
          onClick={() => setShowMobileMenu(false)}
        />
      )}

      {/* ── MOBILE DRAWER ── */}
      <div
        className={`lg:hidden fixed left-0 bottom-0 w-72 bg-white z-[860] flex flex-col transition-transform duration-300 ${
          isTestMode ? 'top-24' : 'top-14'
        } ${showMobileMenu ? 'translate-x-0' : '-translate-x-full'}`}
      >
        {/* Nav Items */}
        <nav className="flex-1 p-4 space-y-1 overflow-y-auto">
          {navigationItems.map((item) => (
            <Link
              key={item.path}
              to={item.path}
              className={`flex items-center gap-3 px-4 py-3 rounded-lg transition-colors whitespace-nowrap cursor-pointer ${
                location.pathname === item.path
                  ? 'bg-teal-50 text-teal-600'
                  : 'text-gray-700 hover:bg-gray-50'
              }`}
            >
              <i className={`${item.icon} text-xl`}></i>
              <span className="font-medium">{item.label}</span>
            </Link>
          ))}
        </nav>

        {/* Test-Modus Toggle */}
        <div className="p-4 border-t border-gray-200">
          <button
            onClick={toggleTestMode}
            className={`w-full flex items-center gap-3 px-4 py-3 rounded-lg transition-colors whitespace-nowrap cursor-pointer ${
              isTestMode ? 'bg-red-50 text-red-600 hover:bg-red-100' : 'text-gray-700 hover:bg-gray-50'
            }`}
          >
            <i className="ri-test-tube-line text-xl"></i>
            <span className="font-medium">{isTestMode ? 'Test-Modus beenden' : 'Test-Modus aktivieren'}</span>
          </button>
        </div>

        {/* User Info & Logout */}
        <div className="p-4 border-t border-gray-200">
          <div className="mb-3 px-4">
            <p className="text-sm font-medium text-gray-900">{session?.name}</p>
            <p className="text-xs text-gray-500">{session?.email}</p>
          </div>
          <button
            onClick={handleLogout}
            className="w-full flex items-center gap-3 px-4 py-3 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors whitespace-nowrap cursor-pointer"
          >
            <i className="ri-logout-box-line text-xl"></i>
            <span className="font-medium">Abmelden</span>
          </button>
        </div>
      </div>

      {/* ── DESKTOP SIDEBAR ── */}
      <aside
        className={`hidden lg:flex fixed left-0 ${isTestMode ? 'top-10' : 'top-0'} bottom-0 w-64 bg-white border-r border-gray-200 flex-col transition-all duration-200`}
      >
        <div className="p-6 border-b border-gray-200">
          <img
            src="https://static.readdy.ai/image/bd37f9b321d8d487bb2df5b36d703d09/c530b467e5a9713a9572ed21607c32d4.png"
            alt="Klosters Logo"
            className="w-full h-auto"
          />
          <h1 className="text-lg font-bold text-gray-900 mt-3">SKI-SERVICE KURIER</h1>
        </div>

        <nav className="flex-1 p-4 space-y-1 overflow-y-auto">
          {navigationItems.map((item) => (
            <Link
              key={item.path}
              to={item.path}
              className={`flex items-center gap-3 px-4 py-3 rounded-lg transition-colors whitespace-nowrap ${
                location.pathname === item.path
                  ? 'bg-teal-50 text-teal-600'
                  : 'text-gray-700 hover:bg-gray-50'
              }`}
            >
              <i className={`${item.icon} text-xl`}></i>
              <span className="font-medium">{item.label}</span>
            </Link>
          ))}
        </nav>

        <div className="p-4 border-t border-gray-200">
          <button
            onClick={toggleTestMode}
            className={`w-full flex items-center gap-3 px-4 py-3 rounded-lg transition-colors whitespace-nowrap ${
              isTestMode ? 'bg-red-50 text-red-600 hover:bg-red-100' : 'text-gray-700 hover:bg-gray-50'
            }`}
          >
            <i className="ri-test-tube-line text-xl"></i>
            <span className="font-medium">{isTestMode ? 'Test-Modus beenden' : 'Test-Modus aktivieren'}</span>
          </button>
        </div>

        <div className="p-4 border-t border-gray-200">
          <div className="mb-3 px-4">
            <p className="text-sm font-medium text-gray-900">{session?.name}</p>
            <p className="text-xs text-gray-500">{session?.email}</p>
          </div>
          <button
            onClick={handleLogout}
            className="w-full flex items-center gap-3 px-4 py-3 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors whitespace-nowrap"
          >
            <i className="ri-logout-box-line text-xl"></i>
            <span className="font-medium">Abmelden</span>
          </button>
        </div>
      </aside>

      {/* ── MAIN CONTENT ── */}
      <main
        className={`lg:ml-64 transition-all duration-200 ${
          isTestMode ? 'lg:pt-10 pt-24' : 'pt-14 lg:pt-0'
        }`}
      >
        <Outlet />
      </main>
    </div>
  );
}
